import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { ADVICE_TEXT } from '../../config/listingsConfig';
import { REDESIGN_COLORS } from '../../styles/colors';
import OverallMatchBadge from './OverallMatchBadge';

/**
 * PersonalisedAdviceBanner - Shows the advice line with the overall match tag
 * (Strong / Solid / Mixed / Limited match + optional modifier).
 *
 * When a price-vs-market delta is supplied, the advice leads with the money
 * reason ("Priced £1,400 under comparable cars, worth a closer look" /
 * "Priced £800 above comparable cars, weigh it carefully"). When no price
 * signal exists it falls back to the score-tier ADVICE_TEXT line.
 *
 * @param {Object} props
 * @param {number} props.score - The match score (0-100)
 * @param {{ direction: 'below'|'above', label: string }|null} [props.priceDelta]
 *   Parsed price-vs-market delta from parsePriceVsMarket. Optional.
 * @param {boolean} [props.isDesktop=false] - Whether to show rounded corners
 */
const PersonalisedAdviceBanner = ({ score, priceDelta = null, isDesktop = false }) => {
  const numericScore = Number(score) || 0;
  const tierAdvice = ADVICE_TEXT.find((a) => numericScore >= a.min) || ADVICE_TEXT[ADVICE_TEXT.length - 1];

  // Score-tier verdict line. The below/above-market money figure is shown once,
  // under the price (sidebar on desktop, sticky bottom bar on mobile), so it is
  // intentionally NOT repeated here.
  const adviceText = tierAdvice.text;

  // Synthetic listing for OverallMatchBadge — banner only knows score, so no
  // modifiers will fire (cohort missing, dom unknown). That's the right
  // behaviour: the banner sits next to the breakdown which already carries
  // signal-level detail; modifiers belong on the feed card.
  const syntheticListing = useMemo(
    () => ({ score: numericScore, listing_snapshot: {} }),
    [numericScore],
  );

  return (
    <View style={[styles.container, isDesktop && styles.containerDesktop]}>
      <View style={styles.textColumn}>
        <Text style={styles.text}>{adviceText}</Text>
        <Text style={styles.label}>Personalised advice</Text>
      </View>
      <OverallMatchBadge listing={syntheticListing} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: REDESIGN_COLORS.cardBg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginHorizontal: -16,
  },
  containerDesktop: {
    borderRadius: 16,
  },
  textColumn: {
    flex: 1,
    marginRight: 12,
    gap: 2,
  },
  text: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: REDESIGN_COLORS.textPrimary,
  },
  label: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    color: REDESIGN_COLORS.textSecondary,
  },
});

export default PersonalisedAdviceBanner;
